import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchServiceAreas, type ServiceArea } from '@/api/client';
import ScrollReveal from '@/components/base/ScrollReveal';
import { useContent } from '@/contexts/ContentContext';

const getQuickLinks = (t: (k: string) => string) => [
  { label: t('nav_home'), href: '#home' },
  { label: t('nav_services'), href: '#services' },
  { label: t('nav_gallery'), href: '#gallery' },
  { label: t('nav_reviews'), href: '#reviews' },
  { label: t('nav_contact'), href: '#contact' },
];

export default function Footer() {
  const { t } = useTranslation('home');
  const { content } = useContent();
  const [areaList, setAreaList] = useState<ServiceArea[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetchServiceAreas().then((data) => {
      if (cancelled) return;
      setAreaList(data);
    });
    return () => { cancelled = true; };
  }, []);

  const quickLinks = getQuickLinks(t);

  const phone = content['company_phone']?.trim() || '(707) 623-6359';
  const email = content['company_email']?.trim() || '';
  const address = content['business_address']?.trim() || 'PO Box 115, Windsor, CA 95492';
  const fbUrl = content['facebook_url']?.trim() || '';
  const igUrl = content['instagram_url']?.trim() || '';
  const yelp = content['yelp_url']?.trim() || '';
  const weekday = content['weekday_hours']?.trim() || '';

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="w-full bg-brand-darker text-white">
      {/* Main footer */}
      <div className="px-4 md:px-6 lg:px-24 max-w-7xl mx-auto py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
          {/* Company Info */}
          <ScrollReveal className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <img
                src="https://static.readdy.ai/image/e50d925263add9c47b947a5709171c8c/d305b02287d3fda18a9e9b5913e18fa7.png"
                alt="Complete Home Care Cleaning logo"
                className="h-8 w-auto object-contain brightness-0 invert"
              />
              <span className="text-sm font-bold font-heading">
                {t('footer_company')}
              </span>
            </div>
            <p className="text-sm text-white/70 leading-relaxed mb-4">
              {t('footer_desc')}
            </p>
            <div className="space-y-2">
              {phone && (
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <i className="ri-phone-line text-brand-mint" />
                  <span>{phone}</span>
                </div>
              )}
              {email && (
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <i className="ri-mail-line text-brand-mint" />
                  <span>{email}</span>
                </div>
              )}
              {address && (
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <i className="ri-map-pin-line text-brand-mint" />
                  <span>{address}</span>
                </div>
              )}
            </div>
          </ScrollReveal>

          {/* Quick Links */}
          <ScrollReveal delay={100}>
            <h4 className="text-sm font-semibold font-heading mb-4 text-brand-mint">
              {t('footer_links')}
            </h4>
            <ul className="space-y-2.5">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="text-sm text-white/70 hover:text-brand-mint transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </ScrollReveal>

          {/* Service Areas */}
          <ScrollReveal delay={200}>
            <h4 className="text-sm font-semibold font-heading mb-4 text-brand-mint">
              {t('footer_areas')}
            </h4>
            <ul className="space-y-2.5">
              {areaList.map((area) => (
                <li key={area.id} className="text-sm text-white/70">
                  {area.name}, {area.state}
                </li>
              ))}
            </ul>
          </ScrollReveal>

          {/* Social */}
          <ScrollReveal delay={300}>
            <h4 className="text-sm font-semibold font-heading mb-4 text-brand-mint">
              {t('footer_social')}
            </h4>
            <div className="flex items-center gap-3 mb-6">
              {fbUrl && (
                <a
                  href={fbUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-brand-mint transition-colors"
                  aria-label={t('facebook_follow')}
                >
                  <i className="ri-facebook-fill text-white" />
                </a>
              )}
              {igUrl && (
                <a
                  href={igUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-brand-mint transition-colors"
                  aria-label={t('admin_instagram_url')}
                >
                  <i className="ri-instagram-line text-white" />
                </a>
              )}
              {yelp && (
                <a
                  href={yelp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-brand-mint transition-colors"
                  aria-label={t('admin_yelp_url')}
                >
                  <i className="ri-star-fill text-white" />
                </a>
              )}
            </div>
            {weekday && (
              <p className="text-xs text-white/50">
                {weekday}
              </p>
            )}
          </ScrollReveal>
        </div>
      </div>

      {/* Copyright bar */}
      <div className="border-t border-white/10">
        <div className="px-4 md:px-6 lg:px-24 max-w-7xl mx-auto py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-white/50">
            {t('footer_copyright')}
          </p>
          <p className="text-xs text-white/50">
            {t('footer_made_with')}
          </p>
        </div>
      </div>
    </footer>
  );
}